import React, { createContext, useContext, useState, useEffect } from "react";

const HotelBookingContext = createContext();

const LOCAL_STORAGE_KEY = "favoriteHotels";

const getInitialFavorites = () => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const initialHotels = [
  {
    id: 1,
    name: "The Ritz-Carlton, Bali",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666153/photo-1566073771259-6a8506099945_lnmnn1.jpg",
    short: "Luxury beachfront resort with world-class amenities.",
    description:
      "The Ritz-Carlton, Bali combines elegant rooms with breathtaking ocean views, gourmet dining, a luxurious spa, and easy access to Bali's cultural landmarks.",
    highlights: [
      "Oceanfront Rooms", "Infinity Pool", "Fine Dining", "Luxury Spa"
    ],
    location: "Bali, Indonesia",
    price: 450,
    tag: "luxury"
  },
  {
    id: 2,
    name: "Shangri-La Tokyo",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666175/Shangri-La-Hotel-Tokyo-Exterior_ahgtmp.jpg",
    short: "5-star hotel in the heart of Tokyo with panoramic city views.",
    description:
      "Stay at Shangri-La Tokyo for effortless luxury with award-winning dining, spacious suites, and exceptional Japanese hospitality close to major attractions.",
    highlights: [
      "Sky Lounge", "Onsen Spa", "City Views", "Michelin Dining"
    ],
    location: "Tokyo, Japan",
    price: 550,
    tag: "premium"
  },
  {
    id: 3,
    name: "Santorini Secret Suites & Spa",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666193/Sunset-views-2_hjzq13.jpg",
    short: "Romantic cliffside suites overlooking the caldera in Santorini.",
    description:
      "Unwind in the infinity pool with caldera views, experience top-rated Mediterranean dining, and stay in beautifully appointed suites for a perfect romantic escape.",
    highlights: [
      "Infinity Pool", "Private Balconies", "Sunset Views", "Couples Spa"
    ],
    location: "Santorini, Greece",
    price: 620,
    tag: "romantic"
  },
  {
    id: 4,
    name: "The Oberoi Udaivilas",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666222/The-Oberoi-Udaivilas_2C-Udaipur__2018_Chandni----the-al-fresco-dining---The-Oberoi-Udaivilas_2C-Udaipur_rkrgrh.jpg",
    short: "A palace-style luxury stay on the banks of Lake Pichola.",
    description:
      "The Oberoi Udaivilas blends regal architecture, ornate decor, traditional Indian hospitality, and stunning lake views for an unforgettable stay.",
    highlights: [
      "Private Pool", "Heritage Suites", "Lake View", "Royal Dining"
    ],
    location: "Udaipur, India",
    price: 380,
    tag: "heritage"
  },
  {
    id: 5,
    name: "Marina Bay Sands",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666479/wp2131889_fqilwu.jpg",
    short: "Iconic luxury hotel with the world’s largest rooftop infinity pool.",
    description:
      "Enjoy breathtaking skyline views from the rooftop, top-class restaurants, a premium casino, and a location close to Singapore's Marina Bay attractions.",
    highlights: [
      "Infinity Pool", "Sky Park", "Fine Dining", "Casino"
    ],
    location: "Singapore",
    price: 570,
    tag: "luxury"
  },
  {
    id: 6,
    name: "Four Seasons Resort Maui",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666835/Maile-Suite-View-1024x712_yxnweu.jpg",
    short: "Elegant Hawaiian resort with world-class service and beachfront access.",
    description:
      "Relax with direct beach access, authentic Hawaiian cultural experiences, and incredible spa treatments at the Four Seasons Resort Maui.",
    highlights: [
      "Beachfront", "Spa", "Hula Shows", "Water Sports"
    ],
    location: "Maui, Hawaii",
    price: 650,
    tag: "beach"
  },
  {
    id: 7,
    name: "Burj Al Arab, Dubai",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666526/burj-al-arab-1-dtcm_ugyipz.jpg",
    short: "Iconic sail-shaped luxury hotel with unparalleled Arabian hospitality.",
    description:
      "Experience the epitome of luxury at Burj Al Arab with private butler service, overwater dining, and one of the most exclusive hotel experiences in the world.",
    highlights: [
      "Private Suites", "Helipad", "Luxury Spa", "Michelin Restaurants"
    ],
    location: "Dubai, UAE",
    price: 2000,
    tag: "ultra-luxury"
  },
  {
    id: 8,
    name: "The Plaza Hotel, New York",
    image: "https://res.cloudinary.com/dyuskzmn3/image/upload/v1751666580/The-Plaza-Hotel-Manhattan-NYC-Photo-Courtesy-The-Plaza-Hotel_tabi7t.jpg",
    short: "Historic Manhattan landmark offering timeless luxury and elegance.",
    description:
      "Enjoy the grandeur of The Plaza Hotel with spacious suites, world-renowned afternoon tea, and classic New York style steps away from Central Park.",
    highlights: [
      "Grand Ballrooms", "Butler Service", "Afternoon Tea", "Central Park Views"
    ],
    location: "New York, USA",
    price: 950,
    tag: "heritage"
  }
];

export const HotelBookingProvider = ({ children }) => {
  const [hotels, setHotels] = useState(initialHotels);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [favorites, setFavorites] = useState(getInitialFavorites);

  const getHotelBySlug = (slug) =>
    hotels.find((hotel) =>
      hotel.name.toLowerCase().replace(/,/g, "").replace(/\s+/g, "-") === slug
    );

  const toggleFavorite = (hotel) => {
    const exists = favorites.some((h) => h.id === hotel.id);
    const updated = exists
      ? favorites.filter((h) => h.id !== hotel.id)
      : [...favorites, hotel];

    setFavorites(updated);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };

  const isFavorite = (hotelId) => favorites.some((h) => h.id === hotelId);

  return (
    <HotelBookingContext.Provider
      value={{
        hotels,
        selectedHotel,
        setSelectedHotel,
        getHotelBySlug,
        favorites,
        toggleFavorite,
        isFavorite,
      }}
    >
      {children}
    </HotelBookingContext.Provider>
  );
};

export const useHotelBookingContext = () => useContext(HotelBookingContext);
